// UserInfoQuestionSetPreviewTab.js
import React, { useState, useRef, useEffect, useMemo } from "react";
import { 
  Row, 
  Col, 
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

import { debounce } from 'lodash';

// Select and DND
import Select, { components } from 'react-select';
import { Plus, X, Heart, MessageSquareMore, Bookmark } from 'lucide-react';
import Toast from "components/Common/Toast"; 

// Placeholders
import placeholderSmall from "assets/images/info/card-placeholder-small.png";

const customSelectStyles = {
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'white',
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
    color: '#495057',
  }),
  control: (provided) => ({
    ...provided,
    backgroundColor: 'white',
    color: 'black',
  }),
};

const QuestionSetPreviewTab = ({
  t,
  infoQuestionSetId,
  infoQuestionSet,
  profileImageUrl,
  imageMap,
  setInfoQuestionSet,
  infoQuestionSetEntries,
  setInfoQuestionSetEntries,
  pushToast,
  setError,
}) => {

  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [selectedPreviewType, setSelectedPreviewType] = useState({ value: "question", label: "題目" });
  const [selectedPreviewQuestion, setSelectedPreviewQuestion] = useState("");
  const [selectedPreviewText, setSelectedPreviewText] = useState("");

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedPreviewImages, setSelectedPreviewImages] = useState([]); // array of _id

  const hasMountedRef = useRef(false);

  useEffect(() => {
    // Load preview data
    if (infoQuestionSet && !hasMountedRef.current) {
      setSelectedPreviewType(infoQuestionSet?.preview?.questionContent ? { value: "question", label: "題目" } :
                             infoQuestionSet?.preview?.textContent ? { value: "text", label: "文字段落" } :
                             { value: "question", label: "題目" });

      setSelectedPreviewQuestion(infoQuestionSet?.preview?.questionContent || "");
      setSelectedPreviewText(infoQuestionSet?.preview?.textContent || "");
      setSelectedPreviewImages(infoQuestionSet?.preview?.imageContents || []);
      hasMountedRef.current = true;
    }
  }, [infoQuestionSet]);


  //--------------------------------------------------
  // Add and Remove
  //--------------------------------------------------

  const handleSelectSingleImage = (imageId) => {
    setSelectedPreviewImages(prev => {
      if (prev.includes(imageId)) return prev; // Skip if already selected
      if (prev.length >= 5) return prev;       // Max 5
      return [...prev, imageId];
    });
    setImageModalOpen(false); // Close modal after one is selected
  };

  const handleRemoveImage = (imageIdToRemove) => {
    setSelectedPreviewImages(prev => prev.filter(id => id !== imageIdToRemove));
  };

  const debouncedSave = useMemo(() => 
    debounce((data) => {
      axiosInstance.patch(`/knovia/myInfo/userInfoQuestionSet/${infoQuestionSetId}/updatePreview`, data)
        .then(() => {
          setInfoQuestionSet(prev => ({
            ...prev,
            preview: data
          }));
        })
        .catch(err => {
          console.error("更新預覽內容失敗:", err);
          setError("預覽儲存失敗，請稍後再試");
        });
    }, 600)
  , [infoQuestionSetId]); // only recreate when infoQuestionSetId changes

  const savePreviewSettings = () => {
    const data = {
      questionContent: selectedPreviewType.value === 'question' ? selectedPreviewQuestion : null,
      textContent: selectedPreviewType.value === 'text' ? selectedPreviewText : null,
      imageContents: selectedPreviewImages
    };
    debouncedSave(data);
  };

  useEffect(() => {
    if (!hasMountedRef.current) {
      return;
    }
    if (!infoQuestionSetId) return;
    savePreviewSettings();
  }, [
    selectedPreviewType,
    selectedPreviewQuestion,
    selectedPreviewText,
    selectedPreviewImages
  ]);


  const handlePushPreview = async () => {
    try {
      const res = await axiosInstance.post(`/knovia/myInfo/userInfoQuestionSet/${infoQuestionSetId}/generatePost`);
      pushToast("預覽已推送！", "success", 3000);
      setInfoQuestionSet(prev => ({
        ...prev,
        previewPostStatus: {
          isPublished: true,
          postId: res.data?._id || null,
          publishedAt: new Date()
        }
      }));
    } catch (err) {
      console.error(err);
      pushToast("推送失敗，請稍後再試", "error", 3000);
    }
  };

  const handleUnpublishPreview = async () => {
    try {
      await axiosInstance.post(`/knovia/myInfo/userInfoQuestionSet/${infoQuestionSetId}/unpublishPost`);
      pushToast("已收回預覽推送！", "success", 3000);
      setInfoQuestionSet(prev => ({
        ...prev,
        previewPostStatus: {
          ...prev.previewPostStatus,
          isPublished: false,
          postId: null,
          publishedAt: null
        }
      }));
    } catch (err) {
      console.error(err);
      pushToast("收回預覽推送失敗，請稍後再試", "error", 3000);
    }
  };

  const handleUpdatePost = async () => {
    try {
      await axiosInstance.post(`/knovia/myInfo/userInfoQuestionSet/${infoQuestionSetId}/updatePost`);
      pushToast("已更新貼文內容！", "success", 3000);
    } catch (err) {
      console.error(err);
      pushToast("更新貼文內容失敗，請稍後再試", "error", 3000);
    }
  };


  return (

    <>
      <Row className="mb-4">
        <Col xs={12}>

          <div style={{ marginBottom: "20px" }}>
            <h5 style={{ marginBottom: "10px" }}>選擇預覽描述</h5>

            <div style={{ marginBottom: "2px" }}>
              <Select
                value={selectedPreviewType}
                onChange={(selectedOption) => setSelectedPreviewType(selectedOption)}
                options={[
                  { value: 'question', label: '題目' },
                  { value: 'text', label: '文字段落' }
                ]}
                styles={customSelectStyles}
              />
            </div>
            <div>
              {selectedPreviewType.value === 'question' && (
                <Select
                  value={[
                    { value: '', label: '— No preview question —' },
                    ...infoQuestionSetEntries
                      .map(q => ({
                        value: q._id,
                        label: `${q.order}. ${q.label}`
                      }))
                  ].find(option => option.value === selectedPreviewQuestion)}
                  onChange={(selectedOption) => setSelectedPreviewQuestion(selectedOption.value)}
                  options={[
                    { value: '', label: '— No preview question —' },
                    ...infoQuestionSetEntries
                      .filter(q => q.type === 'question')
                      .map(q => ({
                        value: q._id,
                        label: `${q.order}. ${q.label}`
                      }))
                  ]}
                  styles={customSelectStyles}
                />
              )}
              {selectedPreviewType.value === 'text' && (
                <Select
                  value={[
                    { value: '', label: '— No preview text —' },
                    ...infoQuestionSetEntries
                      .map(q => ({
                        value: q._id,
                        label: `${q.order}. ${q.text}`
                      }))
                  ].find(option => option.value === selectedPreviewText)}
                  onChange={(selectedOption) => setSelectedPreviewText(selectedOption.value)}
                  options={[
                    { value: '', label: '— No preview text —' },
                    ...infoQuestionSetEntries
                      .filter(q => q.type === selectedPreviewType.value)
                      .map(q => ({
                        value: q._id,
                        label: `${q.order}. ${q.text}`
                      }))
                  ]}
                  styles={customSelectStyles}
                />
              )}
            </div>
          </div>

          <h5 style={{ marginBottom: "10px" }}>預覽</h5>

          <Row className="align-items-center"
            style={{
              border: "1px solid #e2e0e0",
              borderTop: "1.5px solid #ced4da",
              borderBottom: "1.5px solid #ced4da",
              marginBottom: "15px"
            }}
          >
            <Col xs={12} md={6} xl={5} className="mb-2">
              <div>
                <div style={{ margin: "10px 0px 8px 0px" }} className="d-flex justify-content-between">
                  <div className="d-flex">
                    <img src={profileImageUrl || placeholderSmall} alt="preview" 
                      style={{
                        height: "40px",
                        width: "40px",
                        objectFit: "cover",
                        borderRadius: "50%",
                        border: "1px solid #6c97d1",
                        marginRight: "10px"
                      }} 
                    />
                    <div style={{ marginTop: "2.7px", marginRight: "10px" }}>
                      <p className="mb-0">{infoQuestionSet?.label}</p>
                      <p className="mb-0" 
                        style={{ 
                          fontSize: "10.5px",
                          color: "#9b9b9b"
                        }}
                      >{infoQuestionSet?.topic?.label}</p>
                    </div>
                  </div>
                  {infoQuestionSet?.pointReward?.hasReward && 
                    <div className="d-flex align-items-start" style={{ paddingTop: "5px", paddingRight: "10px", minWidth: "80px" }}>
                      <p className="mb-0" style={{ color: "#2f7cc3", fontWeigth: "bold", fontSize: "10.5px" }}>
                        {`TTpoint ${infoQuestionSet?.pointReward?.amount || 0} 點`}
                      </p>
                    </div>
                  }
                </div>

                {selectedPreviewType.value==='question' && (
                  <p style={{ marginTop: "15px", marginBottom: "12px" }}>
                    {infoQuestionSetEntries?.find(q => q._id === selectedPreviewQuestion)?.label || ""}
                  </p>
                )}
                {selectedPreviewType.value==='text' && (
                  <p style={{ marginTop: "15px", marginBottom: "12px" }}>
                    {infoQuestionSetEntries?.find(q => q._id === selectedPreviewText)?.text || ""}
                  </p>
                )}
                
                <div className="scroll-container-no-scrollbar"
                  style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: "5px",
                    marginBottom: "6px", scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  <div className="d-flex align-items-center" style={{ width: "max-content" }}>
                    {selectedPreviewImages.map((imageId, idx) => (
                      <div key={idx} style={{ position: "relative" }}>
                        <img
                          src={imageMap.get(infoQuestionSetEntries.find(q=>q._id===imageId).image) || placeholderSmall}
                          alt={`preview-${idx}`}
                          style={{
                            height: "150px",
                            objectFit: "cover",
                            maxWidth: "180px",
                            borderRadius: "10px",
                            border: "0.5px solid #959595",
                            marginRight: "10px"
                          }}
                        />
                        <div style={{
                          position: "absolute",
                          top: "10px",
                          right: "20px",
                          borderRadius: "50%",
                          backgroundColor: "#808080ad",
                          height: "23px",
                          width: "23px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "white"
                        }}>
                          <X size={14} style={{ cursor: "pointer" }} onClick={() => handleRemoveImage(imageId)} />
                        </div>
                      </div>
                    ))}

                    {selectedPreviewImages.length < 5 && (
                      <div className="d-flex justify-content-center align-items-center"
                        onClick={() => setImageModalOpen(true)}
                        style={{
                          height: "150px",
                          width: "120px",
                          borderRadius: "10px",
                          border: "0.5px solid #959595",
                          marginRight: "10px",
                          backgroundColor: "#f2f2f2",
                          cursor: "pointer"
                        }}
                      >
                        <Plus stroke="#8f8f8f" strokeWidth={1.2} />
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-center" style={{ paddingRight: "10px" }}>
                  <div className="d-flex align-items-center" style={{ marginRight: "8%" }}>
                    <Heart style={{ marginRight:"3px" }} />
                    <p style={{ marginBottom:"3px" }}>10.5K</p>
                  </div>
                  <div className="d-flex align-items-center" style={{ marginRight: "8%" }}>
                    <MessageSquareMore style={{ marginRight:"3px" }} />
                    <p style={{ marginBottom:"3px" }}>263</p>
                  </div>
                  <div className="d-flex align-items-center" >
                    <Bookmark style={{ marginRight:"3px" }} />
                    <p style={{ marginBottom:"3px" }}>57</p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            {infoQuestionSet?.previewPostStatus?.isPublished===false ? (
              <div className="d-flex flex-column align-items-center">
                <Button color="warning" 
                  style={{ minWidth: "150px", maxWidth: "200px", marginBottom: "2px" }}
                  onClick={handlePushPreview}
                >
                  題組預覽推送
                </Button>
                <p 
                  style={{
                    fontSize: "10.5px",
                    color: "#8d8d8d",
                    maxWidth: "190px",
                    textAlign: "center",
                    marginBottom: "3px"
                  }}
                >點選送出後，題組預覽會自動推送至可能有興趣的使用者的動態牆</p>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center">
                <Button color="info" 
                  style={{ minWidth: "150px", maxWidth: "200px", marginBottom: "2px" }}
                  onClick={handleUpdatePost}
                >
                  更新已推送貼文內容
                </Button>
                <Button color="secondary" 
                  style={{ minWidth: "150px", maxWidth: "200px", marginBottom: "2px" }}
                  onClick={handleUnpublishPreview}
                >
                  取消推送
                </Button>
                <p 
                  style={{
                    fontSize: "10.5px",
                    color: "#8d8d8d",
                    maxWidth: "190px",
                    textAlign: "center",
                    marginBottom: "3px"
                  }}
                >已將題組預覽自動推送至可能有興趣的使用者的動態牆</p>
              </div>
            )}
            
          </Row>

        </Col>
      </Row>

      <Modal isOpen={imageModalOpen} toggle={() => setImageModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setImageModalOpen(false)}>選擇最多 5 張圖片</ModalHeader>
        <ModalBody>
          <Row style={{
            maxHeight: "70vh",
            overflowY: "scroll"
          }}>
            {infoQuestionSetEntries
              .filter(q => q.type === "image" && !selectedPreviewImages.includes(q._id))
              .map(entry => {
                const imgUrl = imageMap.get(entry.image) || placeholderSmall;
                return (
                  <Col key={entry._id} xs={6} md={4} lg={3} className="mb-3">
                    <div
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        padding: "5px",
                        cursor: "pointer",
                        textAlign: "center"
                      }}
                      onClick={() => handleSelectSingleImage(entry._id)}
                    >
                      <img
                        src={imgUrl}
                        alt={entry.label}
                        style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "4px" }}
                      />
                      <p className="mb-0" style={{ fontSize: "12px", marginTop: "4px" }}>{entry.order}</p>
                    </div>
                  </Col>
                );
              })}
          </Row>
        </ModalBody>
        <ModalFooter>
          <p className="text-muted mb-0">點選一張圖片後將自動加入預覽</p>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default withTranslation()(QuestionSetPreviewTab);


